import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Menu,
  X,
  Search,
  AlertTriangle,
  ChevronLeft,
  Home as HomeIcon,
  Info,
  Upload,
  ShieldCheck,
  Lock,
  Send,
  CheckCircle2,
  Loader2,
  ChevronRight,
  LogOut,
  MapPin,
  Phone,
  Calendar,
  Award,
  Clock,
  Fingerprint,
  Users,
  Briefcase,
  FileText,
  Database,
  Bell,
  ClipboardList,
  Activity,
  MessageSquare,
  Folder,
  FolderSearch,
  Landmark,
  Globe,
  Car,
  Banknote,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/app";

const UPLOAD_CODE = "1902";
const OFFICER_CODE = "4409";

const OFFICER_PHOTO =
  "https://images.unsplash.com/photo-1741675121621-df90e3195f9d?crop=entropy&cs=srgb&fm=jpg&q=80&w=640&auto=format&fit=crop";

const SSN_DOC_IMG =
  "https://images.unsplash.com/photo-1554224155-1696413565d3?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200&auto=format&fit=crop";
const SSN_PORTRAIT_IMG =
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=srgb&fm=jpg&q=80&w=400&auto=format&fit=crop";

// Evidence imagery used in the full Case Lookup dossier
const EVIDENCE_IMGS = {
  house1: "https://images.unsplash.com/photo-1565829262357-bf9669de2295?crop=entropy&cs=srgb&fm=jpg&q=80&w=900&auto=format&fit=crop",
  house2: "https://images.unsplash.com/photo-1720065609938-ec0e33ffd9ad?crop=entropy&cs=srgb&fm=jpg&q=80&w=900&auto=format&fit=crop",
  suv: "https://images.unsplash.com/photo-1771584282146-4e654a5a563e?crop=entropy&cs=srgb&fm=jpg&q=80&w=900&auto=format&fit=crop",
  cashInCar: "https://images.pexels.com/photos/32540344/pexels-photo-32540344.jpeg?auto=compress&cs=tinysrgb&w=900",
  bloodInCar: "https://images.unsplash.com/photo-1698034303047-92ce45252796?crop=entropy&cs=srgb&fm=jpg&q=80&w=900&auto=format&fit=crop",
  suvLease: "https://images.pexels.com/photos/31663643/pexels-photo-31663643.jpeg?auto=compress&cs=tinysrgb&w=900",
  warehouseLease: "https://images.pexels.com/photos/7841424/pexels-photo-7841424.jpeg?auto=compress&cs=tinysrgb&w=900",
  darkweb: "https://images.pexels.com/photos/5935787/pexels-photo-5935787.jpeg?auto=compress&cs=tinysrgb&w=900",
  warehouse: "https://images.pexels.com/photos/6997065/pexels-photo-6997065.jpeg?auto=compress&cs=tinysrgb&w=900",
  drugs: "https://images.unsplash.com/photo-1620065692460-d8e110a47ffb?crop=entropy&cs=srgb&fm=jpg&q=80&w=900&auto=format&fit=crop",
  card: "https://images.unsplash.com/photo-1657358845938-2e96ebd29598?crop=entropy&cs=srgb&fm=jpg&q=80&w=900&auto=format&fit=crop",
};

const OFFICER = {
  name: "Jason Miller",
  badge: "FT4409",
  title: "Senior Field Officer",
  division: "Federal Cyber & Field Operations Division",
  clearance: "TOP SECRET // SCI",
  status: "Active Duty",
  office: "Washington, D.C. Field Office",
  serviceYears: 8,
  since: "2018",
  dob: "March 14, 1987",
  age: 38,
  father: "Robert A. Miller",
  mother: "Linda M. Miller",
  address: "2200 Potomac Heights Drive, Arlington, VA 22202",
  phone: "(202) 555-0148",
  email: "j.miller@agency.gov",
  nationality: "United States",
  bloodGroup: "O+",
  emergency: "Linda M. Miller — (202) 555-0132",
};

const GENDERS = ["Male", "Female", "Non-binary", "Other", "Prefer not to say"];

const EMPTY_FORM = {
  name: "",
  address: "",
  dob: "",
  phone: "",
  email: "",
  gender: "",
  ssn: "",
};

/* --------------------------- shared modal shell --------------------------- */
const Modal = ({ title, onClose, children, testid }) =>
  createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      data-testid={testid}
    >
      <div className="absolute inset-0 bg-navy-950/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-navy-900 border border-gold-500/30 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gold-500/20">
          <h2 className="font-serif font-semibold text-lg text-white">{title}</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="modal-close-btn"
            className="text-slate-400 hover:text-gold-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-6 max-h-[70vh] overflow-y-auto">{children}</div>
      </div>
    </div>,
    document.body
  );

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] font-mono uppercase tracking-[0.16em] text-gold-400 mb-1.5">{label}</span>
    {children}
  </label>
);

const inputClass =
  "w-full h-11 px-3 bg-navy-950 border border-white/15 text-white text-sm rounded-sm focus:outline-none focus:border-gold-500 transition-colors";

/* ------------------------------ upload flow ------------------------------- */
const UploadModal = ({ onClose }) => {
  const [step, setStep] = useState("code"); // code | form | done
  const [code, setCode] = useState("");
  const [codeErr, setCodeErr] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const checkCode = (e) => {
    e.preventDefault();
    if (code.trim() === UPLOAD_CODE) {
      setCodeErr("");
      setStep("form");
    } else {
      setCodeErr("Invalid access code. Please try again.");
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    for (const k of Object.keys(EMPTY_FORM)) {
      if (!String(form[k]).trim()) {
        toast.error("Please complete all fields.");
        return;
      }
    }
    setBusy(true);
    try {
      await api.post("/submissions", form);
      setStep("done");
      toast.success("Information submitted securely.");
    } catch {
      toast.error("Submission failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Secure Upload" onClose={onClose} testid="upload-modal">
      {step === "code" && (
        <form onSubmit={checkCode} className="space-y-5" data-testid="upload-code-step">
          <div className="flex items-center gap-3 text-gold-300">
            <Lock className="w-6 h-6" />
            <p className="text-sm text-slate-300">
              This area is restricted. Enter the access code to continue.
            </p>
          </div>
          <Field label="Access Code">
            <input
              type="password"
              inputMode="numeric"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className={inputClass}
              placeholder="••••"
              data-testid="upload-code-input"
            />
          </Field>
          {codeErr && <p className="text-sm text-red-400" data-testid="upload-code-error">{codeErr}</p>}
          <button
            type="submit"
            data-testid="upload-code-submit"
            className="w-full h-11 bg-gold-500 text-navy-900 font-semibold text-sm rounded-sm hover:bg-gold-400 transition-colors"
          >
            Unlock
          </button>
        </form>
      )}

      {step === "form" && (
        <form onSubmit={submit} className="space-y-4" data-testid="upload-form-step">
          <Field label="Full Name">
            <input value={form.name} onChange={set("name")} className={inputClass} data-testid="field-name" />
          </Field>
          <Field label="Full Address">
            <input value={form.address} onChange={set("address")} className={inputClass} data-testid="field-address" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Date of Birth">
              <input type="date" value={form.dob} onChange={set("dob")} className={inputClass} data-testid="field-dob" />
            </Field>
            <Field label="Gender">
              <select value={form.gender} onChange={set("gender")} className={inputClass} data-testid="field-gender">
                <option value="">Select…</option>
                {GENDERS.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Phone Number">
              <input type="tel" value={form.phone} onChange={set("phone")} className={inputClass} data-testid="field-phone" />
            </Field>
            <Field label="Email Address">
              <input type="email" value={form.email} onChange={set("email")} className={inputClass} data-testid="field-email" />
            </Field>
          </div>
          <Field label="Social Security Number (SSN)">
            <input value={form.ssn} onChange={set("ssn")} className={inputClass} placeholder="###-##-####" data-testid="field-ssn" />
          </Field>
          <button
            type="submit"
            disabled={busy}
            data-testid="upload-form-submit"
            className="w-full h-11 bg-gold-500 text-navy-900 font-semibold text-sm rounded-sm hover:bg-gold-400 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {busy ? "Submitting…" : "Submit Securely"}
          </button>
        </form>
      )}

      {step === "done" && (
        <div className="text-center py-6" data-testid="upload-done-step">
          <CheckCircle2 className="w-14 h-14 text-gold-400 mx-auto" />
          <h3 className="mt-4 font-serif font-semibold text-xl text-white">Submission Received</h3>
          <p className="mt-2 text-sm text-slate-300">
            Your information has been securely recorded and will be available in the system.
          </p>
          <button
            onClick={onClose}
            data-testid="upload-done-close"
            className="mt-6 h-11 px-6 bg-gold-500 text-navy-900 font-semibold text-sm rounded-sm hover:bg-gold-400 transition-colors"
          >
            Done
          </button>
        </div>
      )}
    </Modal>
  );
};

/* --------------------------- officer dashboard ---------------------------- */
const DashStat = ({ icon: Icon, label, value }) => (
  <div className="bg-white border border-paper-border p-4 flex items-center gap-3">
    <div className="w-10 h-10 rounded-sm bg-navy-900 flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5 text-gold-400" strokeWidth={1.6} />
    </div>
    <div>
      <div className="font-serif font-bold text-xl text-navy-900 leading-none">{value}</div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">{label}</div>
    </div>
  </div>
);

const BioRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-3 border-b border-paper-border last:border-b-0">
    {Icon ? <Icon className="w-4 h-4 text-gold-600 shrink-0 mt-0.5" strokeWidth={1.6} /> : <span className="w-4 shrink-0" />}
    <span className="text-xs font-mono uppercase tracking-[0.12em] text-slate-500 w-40 shrink-0">{label}</span>
    <span className="text-sm text-navy-900 font-medium">{value}</span>
  </div>
);

const QUICK_ACTIONS = [
  { icon: FileText, label: "Case Files" },
  { icon: ClipboardList, label: "File a Report" },
  { icon: Database, label: "Secure Database" },
  { icon: MessageSquare, label: "Secure Message" },
];

const FIRST_NAMES = ["James","Mary","Robert","Patricia","John","Jennifer","Michael","Linda","David","Elizabeth","William","Barbara","Richard","Susan","Joseph","Jessica","Thomas","Sarah","Charles","Karen","Christopher","Nancy","Daniel","Lisa","Matthew","Betty","Anthony","Sandra","Mark","Ashley","Donald","Kimberly","Steven","Emily","Andrew","Donna","Joshua","Michelle","Kenneth","Carol","Kevin","Amanda","Brian","Dorothy","George","Melissa","Edward","Deborah","Ronald","Stephanie","Timothy","Rebecca","Sharon","Jeffrey","Laura","Ryan","Cynthia","Jacob","Amy","Gary","Angela","Nicholas","Eric","Anna","Jonathan","Brenda","Larry","Nicole","Justin","Samantha","Scott","Katherine","Brandon","Christine","Benjamin","Rachel","Samuel","Carlos","Maria","Jose","Luis","Diego","Mei","Wei","Hiroshi","Omar","Aisha","Fatima","Ivan","Olga","Raj","Priya","Noah","Olivia","Liam","Sophia","Ethan","Isabella"];
const LAST_NAMES = ["Smith","Johnson","Williams","Brown","Jones","Garcia","Miller","Davis","Rodriguez","Martinez","Hernandez","Lopez","Gonzalez","Wilson","Anderson","Thomas","Taylor","Moore","Jackson","Martin","Lee","Perez","Thompson","White","Harris","Sanchez","Clark","Ramirez","Lewis","Robinson","Walker","Young","Allen","King","Wright","Scott","Torres","Nguyen","Hill","Flores","Green","Adams","Nelson","Baker","Hall","Rivera","Campbell","Mitchell","Carter","Roberts","Patel","Kim","Chen","Khan","Ali","Petrov","Novak","Okafor","Santos","Reyes"];
const CITIES = [["New York","NY"],["Los Angeles","CA"],["Chicago","IL"],["Houston","TX"],["Phoenix","AZ"],["Philadelphia","PA"],["San Antonio","TX"],["San Diego","CA"],["Dallas","TX"],["Miami","FL"],["Seattle","WA"],["Denver","CO"],["Boston","MA"],["Atlanta","GA"],["Detroit","MI"],["Portland","OR"],["Las Vegas","NV"],["Baltimore","MD"],["Arlington","VA"],["Newark","NJ"],["Austin","TX"],["Columbus","OH"],["Charlotte","NC"],["Memphis","TN"]];
const OFFENSES = ["Wire Fraud","Identity Theft","Cyber Intrusion","Money Laundering","Tax Evasion","Narcotics Trafficking","Securities Fraud","Bank Fraud","Counterfeiting","Smuggling","Extortion","Data Breach","Espionage","Mail Fraud","Racketeering","Bribery","Human Trafficking","Arms Trafficking","Credit Card Fraud","Insider Trading"];
const CASE_STATUSES = ["Open","In Review","Pending","Closed","Under Surveillance"];
const CASE_PRIORITIES = ["Low","Medium","High","Critical"];

// Deterministic PRNG (mulberry32) so the generated case files are stable across renders
const makeRand = (seed) => () => {
  seed |= 0;
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const CASE_FILES = (() => {
  const rand = makeRand(90210);
  const pick = (a) => a[Math.floor(rand() * a.length)];
  return Array.from({ length: 54 }).map((_, i) => {
    const gender = pick(["Male", "Female"]);
    const [city, st] = pick(CITIES);
    return {
      ref: `CF-2026-${String(500 + i).padStart(4, "0")}`,
      name: `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`,
      gender,
      age: 22 + Math.floor(rand() * 44),
      location: `${city}, ${st}`,
      subject: pick(OFFENSES),
      priority: pick(CASE_PRIORITIES),
      status: pick(CASE_STATUSES),
    };
  });
})();

const ageFromDob = (dob) => {
  const y = parseInt(String(dob).slice(0, 4), 10);
  return Number.isFinite(y) ? Math.max(0, 2026 - y) : "—";
};

const _fmtLongDate = (d) => d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
const _daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return _fmtLongDate(d);
};
const SSN_ALERTS = [
  { agency: "Texas State Troopers (TX DPS)", date: _daysAgo(28), note: "Issued statewide BOLO advisory; subject flagged for interstate flight risk under Tex. Penal Code § 38.04 (evading arrest/detention)." },
  { agency: "IRS Criminal Investigation & FinCEN (AML)", date: _daysAgo(25), note: "Suspicious Activity Report (SAR) filed for transaction structuring and money laundering — 31 U.S.C. § 5324, Bank Secrecy Act / AML directive." },
  { agency: "Federal Bureau of Investigation (FBI)", date: _daysAgo(21), note: "Counter-fraud field investigation opened; identifier flagged under 18 U.S.C. § 1028A (aggravated identity theft) and § 1030 (computer fraud)." },
  { agency: "U.S. Department of Justice (DOJ)", date: _daysAgo(17), note: "Federal prosecution authorized; matter referred under 18 U.S.C. § 371 (conspiracy) and § 1343 (wire fraud)." },
];
const SSN_CASE_NO = "DELTA#884";

// Deterministic, fixed case number per SSN (same SSN always yields the same number)
const caseNumberForSsn = (ssn) => {
  const digits = String(ssn || "").replace(/\D/g, "");
  if (!digits) return SSN_CASE_NO;
  let h = 0;
  for (let i = 0; i < digits.length; i++) h = (h * 31 + digits.charCodeAt(i)) >>> 0;
  return `DELTA#${100 + (h % 900)}`;
};

/* ---------------------- case lookup dossier generator --------------------- */
const DOSSIER_BANKS = ["JPMorgan Chase","Bank of America","Wells Fargo","Citibank","U.S. Bank","PNC Bank","Truist Financial","Capital One","TD Bank","Fifth Third Bank","Regions Bank","KeyBank","Citizens Bank","Huntington National","BMO Harris","Ally Bank","Comerica","Zions Bancorp"];
const OFFSHORE_BANKS = [["Deltec Bank & Trust","Nassau, Bahamas"],["Cayman National Bank","George Town, Cayman Is."],["Julius Baer","Geneva, Switzerland"],["DBS Private Bank","Singapore"],["Banco General","Panama City, Panama"],["UBS AG","Zurich, Switzerland"],["HSBC Private","Hong Kong SAR"],["Banque Havilland","Luxembourg"]];
const DARKWEB_ACTS = [
  "SSN listed for sale on darknet marketplace 'SilkVault' — 14 confirmed buyers.",
  "Used to open 9 synthetic-identity credit lines via Tor-hosted brokers.",
  "Linked to a bulk PII dump (2.3M records) traded on encrypted forum 'HydraLeaks'.",
  "Associated with a crypto-mixer wallet laundering ~$1.2M in BTC.",
  "Credentials reused in 31 account-takeover (ATO) attacks on financial portals.",
  "'Fullz' package (SSN + DOB + MMN) resold across 6 dark-web vendors.",
  "Tied to fraudulent unemployment-benefit claims filed in 4 states.",
];
const CRIMINAL_ACTS = [
  "Interstate transport of controlled substances — 21 U.S.C. § 841",
  "Conspiracy to commit wire fraud — 18 U.S.C. § 1349",
  "Aggravated identity theft — 18 U.S.C. § 1028A",
  "Money laundering through shell corporations — 18 U.S.C. § 1956",
  "Smuggling of bulk currency across the southern border — 31 U.S.C. § 5332",
  "Possession of fraudulent identification documents — 18 U.S.C. § 1028",
  "Structuring cash deposits to evade reporting — 31 U.S.C. § 5324",
  "Trafficking in counterfeit access devices — 18 U.S.C. § 1029",
];
const STREETS_A = ["Mesa","Paisano","Alameda","Montana","Yandell","Dyer","Trowbridge"];
const STREETS_B = ["Doniphan","Resler","Mesquite","Transmountain","Pebble Hills","Zaragoza"];

const SYNOPSIS_STEPS = [
  {
    agency: "Texas Dept. of Public Safety (State Troopers)",
    tag: "Origin · Recovery",
    text: "Troopers recovered an abandoned black SUV (good condition) near the El Paso, TX port of entry along the U.S.–Mexico border. An inventory search yielded $4,000,000 in unregistered U.S. currency, apparent blood evidence (submitted to the DPS Crime Laboratory for forensic/DNA analysis), a vehicle lease agreement, and a commercial warehouse lease agreement — all executed under the subject's SSN.",
  },
  {
    agency: "IRS-Criminal Investigation & FinCEN (AML)",
    tag: "Referral · Money Laundering",
    text: "Given the volume of bulk cash, TX DPS referred the matter to IRS-CI and FinCEN under Bank Secrecy Act / Anti-Money-Laundering authorities — 31 U.S.C. § 5332 (bulk cash smuggling) and § 5324 (structuring).",
  },
  {
    agency: "Federal Bureau of Investigation (FBI)",
    tag: "Narcotics Seizure",
    text: "Acting on the warehouse lease recovered from the vehicle, agents searched the premises and seized uncut fentanyl and additional controlled substances. The seizure was referred to the FBI; a joint FBI / DEA / AML task-force investigation was opened under 21 U.S.C. § 841.",
  },
  {
    agency: "Federal Trade Commission (FTC)",
    tag: "Identity Exploitation",
    text: "During record exploitation, the subject's SSN was further identified in dark-web identity-trafficking activity, reported by the FTC under identity-theft authorities — 18 U.S.C. § 1028A.",
  },
];

const _hhmm = (rand) => {
  const h = Math.floor(rand() * 24);
  const m = Math.floor(rand() * 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

// Deterministic investigation dossier derived from the subject's SSN
const buildDossier = (record) => {
  const ssnDigits = String(record?.ssn || "").replace(/\D/g, "");
  let seed = 0;
  for (let i = 0; i < ssnDigits.length; i++) seed = (seed * 31 + ssnDigits.charCodeAt(i)) >>> 0;
  const rand = makeRand(seed || 12345);
  const pick = (a) => a[Math.floor(rand() * a.length)];
  const num = (min, max) => min + Math.floor(rand() * (max - min + 1));
  const take = (a, n) => {
    const c = [...a];
    for (let i = c.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [c[i], c[j]] = [c[j], c[i]];
    }
    return c.slice(0, n);
  };
  const phone = () => `+1 (${num(201, 989)}) ${num(201, 989)}-${String(num(0, 9999)).padStart(4, "0")}`;
  const acct = () => String(num(1000000, 9999999)) + String(num(100, 999));
  const routing = () => String(num(10, 39)) + String(num(1000000, 9999999));

  const phones = Array.from({ length: 5 }, phone);
  const addresses = [
    {
      line: `${num(100, 9999)} ${pick(STREETS_A)} ${pick(["Dr","Ave","Blvd","St"])}, El Paso, TX ${num(79900, 79999)}`,
      img: EVIDENCE_IMGS.house1,
      note: "Property leased under the subject's SSN — no utility or occupancy records filed.",
    },
    {
      line: `${num(100, 9999)} ${pick(STREETS_B)} ${pick(["Dr","Ave","Rd"])}, El Paso, TX ${num(79900, 79999)}`,
      img: EVIDENCE_IMGS.house2,
      note: "Registered residence — flagged vacant during field surveillance.",
    },
  ];
  const usAccounts = take(DOSSIER_BANKS, 14).map((bank) => ({ bank, acct: acct(), routing: routing() }));
  const offshore = take(OFFSHORE_BANKS, 4).map(([bank, loc]) => ({ bank, loc, acct: `IBAN ${pick(["CH","BS","KY","PA","LU","SG"]) }${num(10, 99)} ${num(1000, 9999)} ${num(1000, 9999)} ${num(1000, 9999)}` }));
  const darkweb = take(DARKWEB_ACTS, 4);
  const crimes = take(CRIMINAL_ACTS, 5);

  const callLog = [
    { agency: "Federal Bureau of Investigation (FBI)", number: "1-800-225-5324", when: `Yesterday, ${_hhmm(rand)}`, status: "Call Rejected" },
    { agency: "Federal Trade Commission (FTC)", number: "1-877-382-4357", when: `Yesterday, ${_hhmm(rand)}`, status: "No Answer" },
    { agency: "Internal Revenue Service (IRS-CI)", number: "1-800-829-1040", when: `2 days ago, ${_hhmm(rand)}`, status: "Call Declined" },
    { agency: "U.S. Department of Justice (DOJ)", number: "1-202-514-2000", when: `2 days ago, ${_hhmm(rand)}`, status: "Call Rejected" },
  ];
  const email = record?.email || "unknown@mailbox";
  const emailLog = [
    { from: "notice@ic3.fbi.gov", to: email, when: `Yesterday, ${_hhmm(rand)}`, status: "Bounced — recipient rejected" },
    { from: "compliance@ftc.gov", to: email, when: `2 days ago, ${_hhmm(rand)}`, status: "Returned — mailbox unavailable" },
    { from: "ci-notice@irs.gov", to: email, when: `2 days ago, ${_hhmm(rand)}`, status: "Delivery failed — blocked" },
  ];

  return {
    phones,
    addresses,
    usAccounts,
    offshore,
    darkweb,
    crimes,
    callLog,
    emailLog,
    cash: "$4,000,000",
  };
};

/* ------------------- case documents generator (per SSN) ------------------- */
// Same document set for every case; only the subject name/SSN and reference
// numbers change. Deterministic from the SSN so values stay fixed per case.
const _docDate = (rand, maxDaysAgo) => {
  const d = new Date();
  d.setDate(d.getDate() - Math.floor(rand() * maxDaysAgo));
  return _fmtLongDate(d);
};

const buildDocuments = (record, caseNo, dossier) => {
  const ssnDigits = String(record?.ssn || "").replace(/\D/g, "");
  let seed = 7;
  for (let i = 0; i < ssnDigits.length; i++) seed = (seed * 33 + ssnDigits.charCodeAt(i)) >>> 0;
  const rand = makeRand(seed || 24680);
  const num = (min, max) => min + Math.floor(rand() * (max - min + 1));
  const money = (min, max) => `$${num(min, max).toLocaleString("en-US")}`;
  const ref = (p) => `${p}-2026-${String(num(10000, 99999))}`;

  const name = record.name;
  const last4 = ssnDigits.slice(-4);
  const maskedSsn = `XXX-XX-${last4}`;
  const addr1 = dossier?.addresses?.[0]?.line || record.address;
  const addr2 = dossier?.addresses?.[1]?.line || record.address;
  const whAddr = `${num(100, 9999)} Industrial Park Rd, Unit ${num(1, 40)}, El Paso, TX ${num(79900, 79999)}`;

  return [
    {
      id: "warehouse-lease",
      title: "Commercial Warehouse Lease Agreement",
      category: "Property Record · Evidence Exhibit",
      agency: "Recovered Evidence — El Paso County, TX",
      refNo: ref("WHL"),
      date: _docDate(rand, 540),
      intro: `This Commercial Lease Agreement ("Agreement") is made and entered into as of the date set forth below, by and between the Lessor and the Lessee identified herein, for the premises described below.`,
      parties: [["Lessor", `Rio Grande Industrial Holdings, LLC`], ["Lessee", `${name} — SSN ${maskedSsn}`]],
      fields: [["Premises", whAddr], ["Permitted Use", "General warehousing & storage"], ["Lease Term", `${num(24, 60)} months`], ["Monthly Rent", money(6500, 14500)], ["Security Deposit", money(12000, 30000)]],
      clauses: [
        ["1. Grant of Lease", "Lessor leases to Lessee, and Lessee leases from Lessor, the above-described premises, together with all appurtenances, for the term and upon the conditions set forth herein."],
        ["2. Rent & Payment", "Lessee shall pay the monthly rent in advance on the first day of each calendar month, without demand, deduction, or set-off, at the address designated by Lessor."],
        ["3. Use of Premises", "The premises shall be used solely for the permitted use stated above and for no unlawful purpose. Lessee shall comply with all federal, state, and local laws and ordinances."],
        ["4. Access & Inspection", "Lessor and its authorized agents may enter the premises at reasonable times to inspect, maintain, or exhibit the premises, and as required by lawful order or warrant."],
      ],
      signatories: [["Lessee", name], ["Lessor (Authorized Agent)", "R. Castellanos"]],
    },
    {
      id: "suv-lease",
      title: "Motor Vehicle Lease Agreement (SUV)",
      category: "Vehicle Record · Evidence Exhibit",
      agency: "Recovered Evidence — Vehicle File",
      refNo: ref("MVL"),
      date: _docDate(rand, 420),
      intro: `This Motor Vehicle Lease Agreement ("Agreement") is executed by and between the Lessor (dealer/finance party) and the Lessee named below for the lease of the motor vehicle described herein.`,
      parties: [["Lessor", "Borderland Motors Leasing, Inc."], ["Lessee", `${name} — SSN ${maskedSsn}`]],
      fields: [["Vehicle", `${num(2022, 2025)} Full-Size SUV, Black`], ["VIN", `1F${num(100000, 999999)}XT${num(10000, 99999)}`], ["Lease Term", `${num(36, 60)} months`], ["Monthly Payment", money(780, 1450)], ["Capitalized Cost", money(62000, 94000)]],
      clauses: [
        ["1. Lease of Vehicle", "Lessor leases the described vehicle to Lessee for personal/commercial use for the term stated, subject to the mileage and condition terms herein."],
        ["2. Payments", "Lessee shall make all monthly payments on the due date. Late payments are subject to a finance charge permitted by applicable law."],
        ["3. Insurance", "Lessee shall maintain comprehensive and liability insurance on the vehicle at all times during the lease term, naming Lessor as additional insured."],
        ["4. Title & Return", "Title remains with Lessor. Upon termination, Lessee shall return the vehicle in good condition, ordinary wear excepted."],
      ],
      signatories: [["Lessee", name], ["Lessor (Finance Officer)", "D. Marroquín"]],
    },
    {
      id: "property1-lease",
      title: "Residential Lease Agreement — Property No. 1",
      category: "Property Record · Evidence Exhibit",
      agency: "Recovered Evidence — Property File",
      refNo: ref("RL1"),
      date: _docDate(rand, 600),
      intro: `This Residential Lease Agreement is entered into between the Landlord and the Tenant identified below for the dwelling unit described herein.`,
      parties: [["Landlord", "Sun City Residential Management"], ["Tenant", `${name} — SSN ${maskedSsn}`]],
      fields: [["Premises", addr1], ["Lease Term", `${num(12, 24)} months`], ["Monthly Rent", money(1200, 2600)], ["Security Deposit", money(1200, 3000)], ["Occupancy", "Single family"]],
      clauses: [
        ["1. Premises", "Landlord leases to Tenant the residential premises described above for use as a private dwelling only."],
        ["2. Rent", "Tenant shall pay the monthly rent in advance. A late fee applies to any rent not received within the grace period."],
        ["3. Utilities & Maintenance", "Tenant is responsible for utilities unless otherwise agreed and shall keep the premises clean and in good condition."],
        ["4. Compliance", "Tenant shall comply with all applicable laws and shall not use the premises for any unlawful activity."],
      ],
      signatories: [["Tenant", name], ["Landlord (Agent)", "M. Ibarra"]],
    },
    {
      id: "property2-lease",
      title: "Residential Lease Agreement — Property No. 2",
      category: "Property Record · Evidence Exhibit",
      agency: "Recovered Evidence — Property File",
      refNo: ref("RL2"),
      date: _docDate(rand, 500),
      intro: `This Residential Lease Agreement is entered into between the Landlord and the Tenant identified below for the dwelling unit described herein.`,
      parties: [["Landlord", "Franklin Mountain Properties, LLC"], ["Tenant", `${name} — SSN ${maskedSsn}`]],
      fields: [["Premises", addr2], ["Lease Term", `${num(12, 24)} months`], ["Monthly Rent", money(1300, 2800)], ["Security Deposit", money(1300, 3200)], ["Occupancy", "Single family"]],
      clauses: [
        ["1. Premises", "Landlord leases to Tenant the residential premises described above for use as a private dwelling only."],
        ["2. Rent", "Tenant shall pay the monthly rent in advance on the first day of each month at the office of the Landlord."],
        ["3. Right of Entry", "Landlord may enter upon reasonable notice for inspection, repairs, or as required by lawful order."],
        ["4. Default", "Any breach of the terms herein may result in termination and all remedies available under Texas law."],
      ],
      signatories: [["Tenant", name], ["Landlord (Agent)", "T. Vega"]],
    },
    {
      id: "search-warrant",
      title: "Federal Search & Seizure Warrant",
      category: "Court Instrument",
      agency: "U.S. District Court — Western District of Texas",
      refNo: ref("SW"),
      date: _docDate(rand, 30),
      intro: `To any authorized law enforcement officer: An application by a federal law enforcement officer requests the search of the premises and property identified below. The Court finds probable cause to search and seize the property described.`,
      parties: [["Subject", `${name} — SSN ${maskedSsn}`], ["Issuing Magistrate", "Hon. A. Whitfield, U.S. Magistrate Judge"]],
      fields: [["Case Reference", caseNo], ["Premises to be Searched", whAddr], ["Property to be Seized", "Currency, controlled substances, records, electronic media"], ["Authority", "Fed. R. Crim. P. 41"]],
      clauses: [
        ["1. Authorization", "You are commanded to execute this warrant on or before the date stated and to conduct the search in the daytime unless otherwise authorized."],
        ["2. Return", "The officer executing this warrant shall promptly return it, together with an inventory of any property seized, to the issuing court."],
        ["3. Scope", "The search is limited to the premises described and the property identified as subject to seizure under applicable federal law."],
      ],
      signatories: [["U.S. Magistrate Judge", "A. Whitfield"], ["Affiant (Special Agent)", "Redacted"]],
    },
    {
      id: "seizure-notice",
      title: "Notice of Asset Seizure & Forfeiture",
      category: "Administrative Notice",
      agency: "U.S. Department of the Treasury — FinCEN",
      refNo: ref("ASF"),
      date: _docDate(rand, 20),
      intro: `NOTICE IS HEREBY GIVEN that the property described below has been seized for forfeiture pursuant to federal law. Any person asserting an interest in the property may contest the forfeiture as provided by statute.`,
      parties: [["Respondent", `${name} — SSN ${maskedSsn}`], ["Seizing Agency", "U.S. Treasury / FinCEN"]],
      fields: [["Case Reference", caseNo], ["Seized Currency", "$4,000,000 (unregistered U.S. currency)"], ["Statutory Basis", "31 U.S.C. § 5332; 18 U.S.C. § 981"], ["Date of Seizure", _docDate(rand, 20)]],
      clauses: [
        ["1. Seizure", "The above-described property was seized incident to an ongoing federal investigation and is being held pending forfeiture proceedings."],
        ["2. Right to Contest", "A claimant may file a claim contesting the forfeiture within the statutory period. Failure to do so may result in administrative forfeiture."],
        ["3. Custody", "The property shall remain in the custody of the seizing agency or its designee until final disposition."],
      ],
      signatories: [["Forfeiture Counsel", "J. Hollister"], ["Seizing Officer", "Redacted"]],
    },
    {
      id: "custody-record",
      title: "Evidence Custody & Chain-of-Custody Record",
      category: "Evidence Control",
      agency: "Federal Bureau of Investigation (FBI)",
      refNo: ref("ECR"),
      date: _docDate(rand, 18),
      intro: `This record documents the receipt, handling, and transfer of evidence associated with the referenced investigation. Each transfer of custody shall be recorded with the date, time, and signatures of the releasing and receiving parties.`,
      parties: [["Subject of Record", `${name} — SSN ${maskedSsn}`], ["Custodian", "FBI Evidence Control Unit"]],
      fields: [["Case Reference", caseNo], ["Items", "Currency, narcotics, cloned ID, blood sample, lease documents"], ["Storage Location", `Vault ${num(1, 9)}-${num(100, 999)}`], ["Condition", "Sealed / Logged"]],
      clauses: [
        ["1. Receipt", "All items were received, inventoried, and sealed by the custodian at the date and time indicated."],
        ["2. Chain of Custody", "Every transfer of the evidence shall be documented. Unbroken custody must be maintained to preserve admissibility."],
        ["3. Access", "Access to the evidence is restricted to authorized personnel and shall be logged at each instance."],
      ],
      signatories: [["Releasing Officer", "Redacted"], ["Receiving Custodian", "L. Arroyo"]],
    },
    {
      id: "nda",
      isNda: true,
      title: "Non-Disclosure Agreement",
      category: "LAW ENFORCEMENT SENSITIVE — Confidential",
      agency: "U.S. Department of Justice",
      refNo: ref("DOJ-NDA"),
      date: _docDate(rand, 5),
      intro: `This Non-Disclosure Agreement ("Agreement") is entered into by and between the United States Department of Justice ("the Department") and the undersigned individual identified below ("the Recipient"), in connection with the federal investigation referenced herein.`,
      parties: [["Department", "U.S. Department of Justice"], ["Recipient", `${name} — SSN ${maskedSsn}`]],
      fields: [["Case Reference", caseNo], ["Classification", "LAW ENFORCEMENT SENSITIVE"], ["Effective Date", _docDate(rand, 5)]],
      clauses: [
        ["1. Confidential Information", "The Recipient acknowledges access to confidential, law-enforcement-sensitive information relating to the referenced investigation, including identities, evidence, and investigative methods."],
        ["2. Non-Disclosure", "The Recipient shall not disclose, publish, or communicate any Confidential Information to any person or entity not authorized by the Department, in any form or medium, whether during or after the investigation."],
        ["3. Permitted Use", "Confidential Information shall be used solely for the purposes authorized by the Department and for no other purpose."],
        ["4. Penalties", "Unauthorized disclosure may subject the Recipient to civil and criminal penalties under applicable federal law, including 18 U.S.C. § 1905."],
        ["5. Acknowledgement", "By applying an electronic signature below and uploading the executed copy, the Recipient affirms that they have read, understood, and agree to be bound by this Agreement."],
      ],
      signatories: [["Recipient", name], ["Department Official", "For the U.S. Department of Justice"]],
    },
  ];
};

const ACTIVITY = [
  { text: "Accessed case file CF-2026-0481", time: "2 min ago" },
  { text: "Submitted field report FR-1180", time: "Today, 09:14" },
  { text: "Cleared evidence lock E-233", time: "Yesterday, 17:40" },
  { text: "Security clearance re-verified", time: "3 days ago" },
];

const PRIORITY_STYLE = {
  Critical: "bg-red-100 text-red-700",
  High: "bg-amber-100 text-amber-700",
  Medium: "bg-sky-100 text-sky-700",
  Low: "bg-slate-100 text-slate-600",
};
const STATUS_STYLE = {
  Open: "text-emerald-700",
  "In Review": "text-amber-700",
  Pending: "text-sky-700",
  "Under Surveillance": "text-purple-700",
  Closed: "text-slate-500",
  New: "text-gold-700",
};

const FlagMark = ({ className }) => (
  <svg viewBox="0 0 72 48" className={className} aria-hidden="true">
    <rect width="72" height="48" fill="#ffffff" />
    {[0, 2, 4, 6, 8, 10, 12].map((n) => (
      <rect key={n} y={n * 3.692} width="72" height="3.692" fill="#b22234" />
    ))}
    <rect width="28.8" height="25.84" fill="#3c3b6e" />
  </svg>
);

const FlagWatermark = () => (
  <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden opacity-[0.07]" aria-hidden="true">
    <div className="absolute inset-[-25%] flex flex-wrap gap-10 content-center justify-center" style={{ transform: "rotate(-8deg)" }}>
      {Array.from({ length: 220 }).map((_, i) => (
        <FlagMark key={i} className="w-16 h-auto" />
      ))}
    </div>
  </div>
);

const DetailLine = ({ label, value }) => (
  <div className="flex gap-2">
    <span className="text-slate-500 w-28 shrink-0">{label}</span>
    <span className="text-navy-900 font-medium break-words">{value}</span>
  </div>
);

const SsnSecurityHold = ({ caseNo }) => (
  <div className="border-t border-paper-border bg-red-50 p-6" data-testid="ssn-security-alert">
    <div className="flex items-center gap-2 text-red-700">
      <AlertTriangle className="w-5 h-5 shrink-0" />
      <span className="font-serif font-bold text-sm tracking-wide">ACTIVE MULTI-AGENCY SECURITY HOLD</span>
    </div>
    <p className="mt-2 text-xs text-red-800/90 leading-relaxed">
      This Social Security Number is currently <span className="font-semibold">UNDER ACTIVE FEDERAL SECURITY REVIEW</span>. The identifier has been flagged across
      multiple jurisdictions and is subject to an interagency watch directive and a temporary benefits/transaction freeze. All access to this record is logged and
      monitored pursuant to 5 U.S.C. § 552a (Privacy Act).
    </p>
    <div className="mt-3 divide-y divide-red-200">
      {SSN_ALERTS.map((a) => (
        <div key={a.agency} className="py-2">
          <div className="flex items-start justify-between gap-3">
            <span className="text-xs font-semibold text-red-900">Reported by {a.agency}</span>
            <span className="font-mono text-[10px] text-red-600 shrink-0 whitespace-nowrap">{a.date}</span>
          </div>
          <p className="text-[11px] text-red-700/80 mt-0.5 leading-snug">{a.note}</p>
        </div>
      ))}
    </div>
    <div className="mt-3 flex items-start gap-2 border-t border-red-200 pt-3">
      <FileText className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
      <span className="text-xs text-red-900">
        A federal case has been filed for multiple criminal activities (fraud, money laundering and identity offenses) —{" "}
        <span className="font-mono font-bold text-navy-900 tracking-wider" data-testid="ssn-case-number">Case No. {caseNo}</span>
      </span>
    </div>
  </div>
);

const SsnResultsPage = ({ last4, matches, onBack }) =>
  createPortal(
    <div className="fixed inset-0 z-[130] bg-paper-50 overflow-y-auto" data-testid="ssn-results-page">
      <FlagWatermark />
      <div className="sticky top-0 z-20 bg-navy-950 border-b border-gold-500/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 h-16 flex items-center gap-3 text-white">
          <ShieldCheck className="w-7 h-7 text-gold-400" />
          <div className="leading-none">
            <div className="font-serif font-bold tracking-wide">SOCIAL SECURITY ADMINISTRATION</div>
            <div className="font-mono text-[9px] tracking-[0.22em] text-gold-400 mt-1">SECURE RECORD · SSN ENDING {last4}</div>
          </div>
          <button
            onClick={onBack}
            data-testid="ssn-back"
            className="ml-auto inline-flex items-center gap-2 border border-gold-500/40 text-gold-300 h-9 px-4 rounded-sm text-sm hover:bg-gold-500/10 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
        </div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <div className="relative h-28 w-full overflow-hidden border border-paper-border rounded-sm">
          <img src={SSN_DOC_IMG} alt="" className="w-full h-full object-cover blur-[5px] scale-110" />
          <div className="absolute inset-0 bg-navy-950/55 flex items-center justify-center">
            <div className="text-center">
              <div className="font-mono text-[10px] tracking-[0.24em] text-gold-300">VERIFIED RECORD MATCH</div>
              <div className="font-serif font-bold text-white text-lg">SSN ending in {last4}</div>
            </div>
          </div>
        </div>

        {matches.length === 0 ? (
          <div className="mt-6 bg-white border border-paper-border p-10 text-center" data-testid="ssn-no-match">
            <AlertTriangle className="w-10 h-10 text-slate-300 mx-auto" />
            <h3 className="mt-3 font-serif font-semibold text-lg text-navy-900">No record found</h3>
            <p className="mt-1 text-sm text-slate-500">No uploaded record matches an SSN ending in {last4}.</p>
          </div>
        ) : (
          matches.map((r) => (
            <div key={r.id} className="mt-6 bg-white border border-paper-border overflow-hidden" data-testid="ssn-match-record">
              <div className="flex gap-5 p-6">
                <div className="relative w-28 h-36 shrink-0 overflow-hidden border border-paper-border rounded-sm bg-paper-100">
                  <img src={SSN_PORTRAIT_IMG} alt="" className="w-full h-full object-cover blur-[7px] scale-125" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-mono text-[9px] text-navy-900/60">PHOTO</span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h2 className="font-serif font-bold text-2xl text-navy-900">{r.name}</h2>
                      <span className="mt-1 inline-block font-mono text-xs text-red-700 bg-red-100 px-2 py-0.5 rounded-sm" data-testid="ssn-record-caseno">Case No. {caseNumberForSsn(r.ssn)}</span>
                    </div>
                    <span className="font-mono text-sm text-navy-900 font-semibold shrink-0">{r.ssn}</span>
                  </div>
                  <div className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                    <DetailLine label="Gender" value={r.gender} />
                    <DetailLine label="Date of Birth" value={r.dob} />
                    <DetailLine label="Phone" value={r.phone} />
                    <DetailLine label="Email" value={r.email} />
                    <div className="sm:col-span-2">
                      <DetailLine label="Address" value={r.address} />
                    </div>
                  </div>
                </div>
              </div>
              <SsnSecurityHold caseNo={caseNumberForSsn(r.ssn)} />
            </div>
          ))
        )}
      </div>
    </div>,
    document.body
  );

/* --------------------------- case dossier page ---------------------------- */
const DossierSection = ({ icon: Icon, title, tone = "navy", children, testid }) => (
  <section className="mt-6 bg-white border border-paper-border overflow-hidden" data-testid={testid}>
    <div className={`flex items-center gap-2 px-5 py-3 border-b border-paper-border ${tone === "red" ? "bg-red-50" : "bg-paper-100"}`}>
      <Icon className={`w-5 h-5 ${tone === "red" ? "text-red-600" : "text-gold-600"}`} />
      <h3 className={`font-serif font-bold text-sm tracking-wide ${tone === "red" ? "text-red-800" : "text-navy-900"}`}>{title}</h3>
    </div>
    <div className="p-5">{children}</div>
  </section>
);

const EvidenceImage = ({ src, caption, tag = "EVIDENCE" }) => (
  <figure className="relative overflow-hidden border border-paper-border rounded-sm bg-paper-100">
    <span className="absolute top-2 left-2 z-10 font-mono text-[9px] tracking-[0.18em] text-white bg-red-700/90 px-2 py-0.5 rounded-sm">{tag}</span>
    <img src={src} alt={caption} className="w-full h-40 object-cover" />
    {caption && <figcaption className="px-3 py-2 text-[11px] text-slate-600 bg-white border-t border-paper-border">{caption}</figcaption>}
  </figure>
);

/* ----------------------------- document viewer ---------------------------- */
// Real, public-domain U.S. agency seals (Wikimedia Commons Special:FilePath → PNG)
const SEALS = {
  doj: "https://commons.wikimedia.org/wiki/Special:FilePath/Seal_of_the_United_States_Department_of_Justice.svg?width=400",
  fbi: "https://commons.wikimedia.org/wiki/Special:FilePath/Seal_of_the_Federal_Bureau_of_Investigation.svg?width=400",
  treasury: "https://commons.wikimedia.org/wiki/Special:FilePath/Seal_of_the_United_States_Department_of_the_Treasury.svg?width=400",
  greatseal: "https://commons.wikimedia.org/wiki/Special:FilePath/Great_Seal_of_the_United_States_(obverse).svg?width=400",
};
const DOC_SEAL = {
  "search-warrant": "greatseal",
  "seizure-notice": "treasury",
  "custody-record": "fbi",
  nda: "doj",
};

const DocField = ({ label, value }) => (
  <div className="flex gap-3 py-1.5 border-b border-dashed border-paper-border last:border-0">
    <span className="text-[11px] font-mono uppercase tracking-wide text-slate-500 w-44 shrink-0">{label}</span>
    <span className="text-sm text-navy-900 font-medium break-words">{value}</span>
  </div>
);

const DocumentPage = ({ doc, onBack }) => {
  const [signName, setSignName] = useState("");
  const [signed, setSigned] = useState(false);
  const [fileName, setFileName] = useState("");
  const seal = SEALS[DOC_SEAL[doc.id]] || null;

  const applySignature = () => {
    if (signName.trim().length < 3) {
      toast.error("Enter your full legal name to e-sign.");
      return;
    }
    setSigned(true);
    toast.success("Electronic signature applied.");
  };
  const onUpload = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setFileName(f.name);
      toast.success("Executed copy uploaded for review.");
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[140] bg-navy-950/60 overflow-y-auto" data-testid="document-page">
      <div className="sticky top-0 z-20 bg-navy-950 border-b border-gold-500/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 h-14 flex items-center gap-3 text-white">
          <FileText className="w-6 h-6 text-gold-400" />
          <div className="font-mono text-[10px] tracking-[0.2em] text-gold-400 truncate">OFFICIAL DOCUMENT · {doc.refNo}</div>
          <button
            onClick={onBack}
            data-testid="document-back"
            className="ml-auto inline-flex items-center gap-2 border border-gold-500/40 text-gold-300 h-9 px-4 rounded-sm text-sm hover:bg-gold-500/10 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8">
        <div className="relative bg-white border border-paper-border shadow-xl overflow-hidden" data-testid="document-sheet">
          {/* genuine seal watermark */}
          {seal ? (
            <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center" data-testid="document-watermark">
              <img src={seal} alt="" className="w-[70%] max-w-md opacity-[0.06]" />
            </div>
          ) : (
            <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center overflow-hidden" data-testid="document-watermark">
              <span className="rotate-[-20deg] font-serif font-bold text-5xl sm:text-6xl text-navy-900/[0.05] tracking-widest whitespace-nowrap">RECOVERED EVIDENCE</span>
            </div>
          )}
          {/* certified stamp */}
          {!doc.isNda && (
            <div className="pointer-events-none absolute top-24 right-6 z-20 rotate-[-14deg] border-2 border-red-500/70 text-red-600/80 font-serif font-bold text-xs tracking-widest px-3 py-1 rounded">
              CERTIFIED TRUE COPY
            </div>
          )}
          {doc.isNda && (
            <div className="pointer-events-none absolute top-24 right-6 z-20 rotate-[-14deg] border-2 border-navy-900/50 text-navy-900/70 font-serif font-bold text-[11px] tracking-widest px-3 py-1 rounded">
              LAW ENFORCEMENT SENSITIVE
            </div>
          )}

          {/* letterhead */}
          <div className="relative z-10 text-center px-8 pt-8 pb-5 border-b-2 border-navy-900/15">
            {seal ? (
              <img src={seal} alt={`${doc.agency} seal`} className="w-16 h-16 mx-auto object-contain" data-testid="document-seal" />
            ) : (
              <ShieldCheck className="w-10 h-10 text-navy-900 mx-auto" />
            )}
            <div className="mt-2 font-mono text-[10px] tracking-[0.28em] text-slate-500">UNITED STATES OF AMERICA</div>
            <div className="font-serif font-bold text-lg text-navy-900 mt-0.5">{doc.agency}</div>
            <div className="font-mono text-[9px] tracking-[0.2em] text-red-600 mt-1">{doc.category}</div>
          </div>

          <div className="relative z-10 px-8 py-6">
            <h2 className="font-serif font-bold text-xl text-navy-900 text-center uppercase tracking-wide">{doc.title}</h2>
            <div className="mt-2 flex items-center justify-between font-mono text-[11px] text-slate-500">
              <span>Ref. {doc.refNo}</span>
              <span>Dated: {doc.date}</span>
            </div>

            <p className="mt-5 text-sm text-slate-700 leading-relaxed text-justify">{doc.intro}</p>

            {/* parties */}
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              {doc.parties.map(([role, value]) => (
                <div key={role} className="bg-paper-50 border border-paper-border rounded-sm p-3">
                  <div className="text-[10px] font-mono uppercase tracking-wide text-slate-500">{role}</div>
                  <div className="text-sm text-navy-900 font-semibold mt-0.5">{value}</div>
                </div>
              ))}
            </div>

            {/* fields */}
            <div className="mt-5">
              {doc.fields.map(([label, value]) => (
                <DocField key={label} label={label} value={value} />
              ))}
            </div>

            {/* clauses */}
            <div className="mt-6 space-y-4">
              {doc.clauses.map(([heading, text]) => (
                <div key={heading}>
                  <h4 className="font-serif font-semibold text-sm text-navy-900">{heading}</h4>
                  <p className="text-[13px] text-slate-700 leading-relaxed mt-1 text-justify">{text}</p>
                </div>
              ))}
            </div>

            {/* signatures / NDA actions */}
            {doc.isNda ? (
              <div className="mt-8 border-t-2 border-navy-900/15 pt-6" data-testid="nda-actions">
                <h4 className="font-serif font-bold text-sm text-navy-900">Execution — Electronic Signature Required</h4>
                <p className="text-[12px] text-slate-500 mt-1">
                  This document cannot be downloaded. Apply your electronic signature and upload the executed copy for departmental review.
                </p>

                <div className="mt-4 grid sm:grid-cols-2 gap-5">
                  {/* e-sign */}
                  <div className="bg-paper-50 border border-paper-border rounded-sm p-4">
                    <div className="text-[11px] font-mono uppercase tracking-wide text-slate-500">Recipient E-Signature</div>
                    {signed ? (
                      <div className="mt-3" data-testid="nda-signed">
                        <div className="font-[cursive] text-2xl text-navy-900 border-b border-navy-900/30 pb-1">{signName}</div>
                        <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-emerald-700 font-semibold">
                          <CheckCircle2 className="w-4 h-4" /> Signed electronically · {new Date().toLocaleString("en-US")}
                        </div>
                      </div>
                    ) : (
                      <>
                        <input
                          value={signName}
                          onChange={(e) => setSignName(e.target.value)}
                          placeholder="Type your full legal name"
                          className="mt-3 w-full h-11 bg-white border border-paper-border rounded-sm px-3 text-sm text-navy-900 outline-none focus:border-gold-500"
                          data-testid="nda-sign-input"
                        />
                        <button
                          onClick={applySignature}
                          data-testid="nda-sign-submit"
                          className="mt-3 w-full h-10 bg-navy-900 text-white text-sm font-semibold rounded-sm hover:bg-navy-800 transition-colors inline-flex items-center justify-center gap-2"
                        >
                          <Fingerprint className="w-4 h-4" /> Apply E-Signature
                        </button>
                      </>
                    )}
                  </div>

                  {/* upload */}
                  <div className="bg-paper-50 border border-paper-border rounded-sm p-4">
                    <div className="text-[11px] font-mono uppercase tracking-wide text-slate-500">Upload Executed Copy</div>
                    <label className="mt-3 flex flex-col items-center justify-center gap-2 h-[88px] border-2 border-dashed border-paper-border rounded-sm cursor-pointer hover:border-gold-500 transition-colors" data-testid="nda-upload-label">
                      <Upload className="w-5 h-5 text-slate-400" />
                      <span className="text-[12px] text-slate-500">{fileName ? fileName : "Click to upload signed PDF / scan"}</span>
                      <input type="file" className="hidden" onChange={onUpload} data-testid="nda-upload-input" />
                    </label>
                    {fileName && (
                      <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-emerald-700 font-semibold" data-testid="nda-uploaded">
                        <CheckCircle2 className="w-4 h-4" /> Uploaded for review
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-10 grid sm:grid-cols-2 gap-8" data-testid="document-signatures">
                {doc.signatories.map(([role, who]) => (
                  <div key={role}>
                    <div className="font-[cursive] text-xl text-navy-900/80 h-8">{who === "Redacted" ? "— redacted —" : who}</div>
                    <div className="border-t border-navy-900/50 pt-1 text-[11px] font-mono uppercase tracking-wide text-slate-500">{role}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ------------------------------- doc list tab ----------------------------- */
const DocumentsTab = ({ documents, onOpen }) => (
  <div data-testid="documents-tab">
    <DossierSection icon={FileText} title="Case Documents & Agreements">
      <p className="text-sm text-slate-600 mb-4">
        Official records, recovered agreements, and court instruments associated with this case. Documents are standardized across all cases; subject identifiers are populated per record.
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        {documents.filter((d) => !d.isNda).map((d) => (
          <button
            key={d.id}
            onClick={() => onOpen(d)}
            data-testid={`document-card-${d.id}`}
            className="text-left bg-paper-50 border border-paper-border rounded-sm p-4 hover:border-gold-500 hover:bg-white transition-colors group"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-sm bg-white border border-paper-border flex items-center justify-center shrink-0 overflow-hidden">
                {SEALS[DOC_SEAL[d.id]] ? (
                  <img src={SEALS[DOC_SEAL[d.id]]} alt="" className="w-8 h-8 object-contain" />
                ) : (
                  <FileText className="w-5 h-5 text-navy-900" />
                )}
              </div>
              <div className="min-w-0">
                <div className="font-serif font-semibold text-navy-900 text-sm leading-snug">{d.title}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{d.category}</div>
                <div className="font-mono text-[10px] text-slate-400 mt-1">{d.refNo} · {d.date}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400 ml-auto group-hover:text-gold-600 shrink-0" />
            </div>
          </button>
        ))}
      </div>
    </DossierSection>

    {/* NDA — action required */}
    {documents.filter((d) => d.isNda).map((d) => (
      <section key={d.id} className="mt-6 bg-white border-2 border-red-300 overflow-hidden" data-testid="nda-section">
        <div className="flex items-center gap-2 px-5 py-3 bg-red-600 text-white">
          <Lock className="w-5 h-5" />
          <h3 className="font-serif font-bold text-sm tracking-wide">ACTION REQUIRED · NON-DISCLOSURE AGREEMENT</h3>
        </div>
        <div className="p-5 flex items-start gap-4">
          <div className="w-12 h-12 rounded-sm bg-white border border-paper-border flex items-center justify-center shrink-0 overflow-hidden">
            <img src={SEALS.doj} alt="U.S. Department of Justice seal" className="w-10 h-10 object-contain" />
          </div>
          <div className="flex-1">
            <div className="font-serif font-semibold text-navy-900">{d.title} — {d.agency}</div>
            <p className="text-[13px] text-slate-600 mt-1">
              You must review and electronically sign this Non-Disclosure Agreement, then upload the executed copy. This document cannot be downloaded.
            </p>
            <button
              onClick={() => onOpen(d)}
              data-testid={`document-card-${d.id}`}
              className="mt-3 inline-flex items-center gap-2 h-10 px-5 bg-red-600 text-white text-sm font-semibold rounded-sm hover:bg-red-700 transition-colors"
            >
              <Fingerprint className="w-4 h-4" /> Open &amp; E-Sign NDA
            </button>
          </div>
        </div>
      </section>
    ))}
  </div>
);

/* --------------------------- asset verification --------------------------- */
const ASSET_STATUS = ["Verified", "Frozen", "Under Review", "Seized"];
const AssetVerificationTab = ({ record, d }) => {
  const digits = String(record.ssn).replace(/\D/g, "");
  const seed = digits.split("").reduce((a, c) => (a * 17 + c.charCodeAt(0)) >>> 0, 11);
  const rand = makeRand(seed);
  const status = () => ASSET_STATUS[Math.floor(rand() * ASSET_STATUS.length)];
  const styleFor = (s) =>
    s === "Verified" ? "bg-emerald-100 text-emerald-700" :
    s === "Frozen" ? "bg-sky-100 text-sky-700" :
    s === "Seized" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700";

  const assets = [
    { cat: "Currency", item: "$4,000,000 unregistered U.S. currency", ref: "ASSET-CUR-01", status: "Seized" },
    { cat: "Vehicle", item: "Full-Size SUV (Black) — leased under SSN", ref: "ASSET-VEH-01", status: "Seized" },
    { cat: "Real Property", item: d.addresses[0].line, ref: "ASSET-RE-01", status: status() },
    { cat: "Real Property", item: d.addresses[1].line, ref: "ASSET-RE-02", status: status() },
    { cat: "Real Property", item: "Commercial Warehouse — El Paso, TX", ref: "ASSET-RE-03", status: "Frozen" },
    ...d.usAccounts.slice(0, 6).map((a, i) => ({ cat: "Bank Account", item: `${a.bank} · ****${a.acct.slice(-4)}`, ref: `ASSET-BNK-${String(i + 1).padStart(2, "0")}`, status: status() })),
    ...d.offshore.map((a, i) => ({ cat: "Offshore Account", item: `${a.bank} · ${a.loc}`, ref: `ASSET-OFF-${String(i + 1).padStart(2, "0")}`, status: "Frozen" })),
  ];

  return (
    <div data-testid="assets-tab">
      <DossierSection icon={Landmark} title="Asset Verification Register">
        <p className="text-sm text-slate-600 mb-4">
          Consolidated verification status of all assets associated with the subject's SSN. Statuses reflect current custody / hold determinations.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[620px]">
            <thead>
              <tr className="text-left font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500 border-b border-paper-border">
                <th className="py-2 pr-3 font-normal">Ref</th>
                <th className="py-2 pr-3 font-normal">Category</th>
                <th className="py-2 pr-3 font-normal">Asset</th>
                <th className="py-2 font-normal">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-paper-border">
              {assets.map((a) => (
                <tr key={a.ref} data-testid="asset-row">
                  <td className="py-2.5 pr-3 font-mono text-slate-400">{a.ref}</td>
                  <td className="py-2.5 pr-3 text-slate-600">{a.cat}</td>
                  <td className="py-2.5 pr-3 text-navy-900">{a.item}</td>
                  <td className="py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${styleFor(a.status)}`}>{a.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DossierSection>
    </div>
  );
};

/* --------------------------- victim application --------------------------- */
const VictimApplicationTab = ({ caseNo }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", relationship: "", statement: "" });
  const [submitted, setSubmitted] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.statement.trim()) {
      toast.error("Name and statement are required.");
      return;
    }
    setSubmitted(true);
    toast.success("Victim application submitted for review.");
  };

  const inputClass = "w-full h-11 bg-white border border-paper-border rounded-sm px-3 text-sm text-navy-900 outline-none focus:border-gold-500";

  return (
    <div data-testid="victim-tab">
      <DossierSection icon={Users} title="Victim Application / Impact Statement">
        {submitted ? (
          <div className="text-center py-10" data-testid="victim-submitted">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
            <h3 className="mt-3 font-serif font-bold text-lg text-navy-900">Application Received</h3>
            <p className="mt-1 text-sm text-slate-500">Your victim application for case {caseNo} has been submitted for review by the assigned agency.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-sm text-slate-600">
              Individuals who believe they have been harmed in connection with case {caseNo} may file a victim application. All submissions are reviewed under the Crime Victims' Rights Act (18 U.S.C. § 3771).
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wide text-slate-500">Full Name *</label>
                <input value={form.name} onChange={set("name")} className={inputClass} data-testid="victim-name" />
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wide text-slate-500">Relationship to Case</label>
                <input value={form.relationship} onChange={set("relationship")} placeholder="e.g. Identity theft victim" className={inputClass} data-testid="victim-relationship" />
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wide text-slate-500">Email</label>
                <input value={form.email} onChange={set("email")} className={inputClass} data-testid="victim-email" />
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wide text-slate-500">Phone</label>
                <input value={form.phone} onChange={set("phone")} className={inputClass} data-testid="victim-phone" />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wide text-slate-500">Statement *</label>
              <textarea value={form.statement} onChange={set("statement")} rows={5} className="w-full bg-white border border-paper-border rounded-sm px-3 py-2 text-sm text-navy-900 outline-none focus:border-gold-500 resize-none" data-testid="victim-statement" />
            </div>
            <button type="submit" data-testid="victim-submit" className="h-11 px-6 bg-navy-900 text-white text-sm font-semibold rounded-sm hover:bg-navy-800 transition-colors inline-flex items-center gap-2">
              <Send className="w-4 h-4" /> Submit Application
            </button>
          </form>
        )}
      </DossierSection>
    </div>
  );
};


const CaseDossierPage = ({ caseNo, record, onBack }) => {
  const d = buildDossier(record);
  const last4 = String(record.ssn).replace(/\D/g, "").slice(-4);
  const documents = buildDocuments(record, caseNo, d);
  const [tab, setTab] = useState("dossier");
  const [openDoc, setOpenDoc] = useState(null);
  const TABS = [
    { key: "dossier", label: "Case Dossier", icon: FolderSearch },
    { key: "documents", label: "Documents", icon: FileText },
    { key: "assets", label: "Asset Verification", icon: Landmark },
    { key: "victim", label: "Victim Application", icon: Users },
  ];
  return createPortal(
    <div className="fixed inset-0 z-[130] bg-paper-50 overflow-y-auto" data-testid="case-dossier-page">
      <FlagWatermark />
      <div className="sticky top-0 z-20 bg-navy-950 border-b border-gold-500/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-16 flex items-center gap-3 text-white">
          <FolderSearch className="w-7 h-7 text-gold-400" />
          <div className="leading-none">
            <div className="font-serif font-bold tracking-wide">FEDERAL CASE DOSSIER</div>
            <div className="font-mono text-[9px] tracking-[0.22em] text-gold-400 mt-1" data-testid="dossier-caseno">CASE {caseNo} · CLASSIFIED</div>
          </div>
          <button
            onClick={onBack}
            data-testid="case-dossier-back"
            className="ml-auto inline-flex items-center gap-2 border border-gold-500/40 text-gold-300 h-9 px-4 rounded-sm text-sm hover:bg-gold-500/10 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
        </div>
      </div>

      {/* tab navigation */}
      <div className="sticky top-16 z-20 bg-navy-900 border-b border-gold-500/20" data-testid="dossier-tabs">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 flex items-center gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`dossier-tab-${t.key}`}
              className={`inline-flex items-center gap-2 h-11 px-4 text-xs font-semibold tracking-wide whitespace-nowrap border-b-2 transition-colors ${
                tab === t.key ? "border-gold-400 text-gold-300" : "border-transparent text-slate-400 hover:text-slate-200"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-8 py-8">
        {tab === "documents" && <DocumentsTab documents={documents} onOpen={setOpenDoc} />}
        {tab === "assets" && <AssetVerificationTab record={record} d={d} />}
        {tab === "victim" && <VictimApplicationTab caseNo={caseNo} />}
        {tab === "dossier" && (<>
        {/* case synopsis (shown first) */}
        <section className="bg-white border-2 border-navy-900/15 overflow-hidden" data-testid="dossier-synopsis">
          <div className="flex items-center gap-2 px-5 py-3 bg-navy-950 text-white">
            <FileText className="w-5 h-5 text-gold-400" />
            <h3 className="font-serif font-bold text-sm tracking-wide">CASE SYNOPSIS</h3>
            <span className="ml-auto font-mono text-[10px] text-gold-400">CASE {caseNo}</span>
          </div>
          <div className="p-5">
            <p className="text-sm text-slate-700 leading-relaxed">
              The following is a chronological summary of the interagency federal investigation associated with the
              subject's Social Security Number. All investigative actions are logged pursuant to 5 U.S.C. § 552a (Privacy Act).
            </p>
            <ol className="mt-4 space-y-4">
              {SYNOPSIS_STEPS.map((s, i) => (
                <li key={s.agency} className="flex gap-3" data-testid={`synopsis-step-${i + 1}`}>
                  <span className="shrink-0 w-7 h-7 rounded-full bg-gold-500 text-navy-900 font-mono font-bold text-xs flex items-center justify-center">{i + 1}</span>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-serif font-semibold text-navy-900 text-sm">{s.agency}</span>
                      <span className="font-mono text-[9px] uppercase tracking-wide text-slate-500 bg-paper-100 border border-paper-border px-1.5 py-0.5 rounded-sm">{s.tag}</span>
                    </div>
                    <p className="text-[13px] text-slate-600 mt-1 leading-relaxed">{s.text}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="mt-5 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm p-3" data-testid="synopsis-status">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-[13px] text-amber-900">
                <span className="font-semibold">Current Status:</span> Investigation ACTIVE and ongoing across multiple
                agencies. For consolidated reporting, custody of this matter has been
                <span className="font-semibold"> transferred to the Federal Trade Commission (FTC)</span> pending a complete interagency report.
              </p>
            </div>
          </div>
        </section>

        {/* subject header + comms dialogue box */}
        <div className="grid lg:grid-cols-[1.6fr_1fr] gap-6 mt-6">
          <div className="bg-white border border-paper-border p-6" data-testid="dossier-subject">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-mono text-[10px] tracking-[0.24em] text-red-600">SUBJECT OF INVESTIGATION</div>
                <h2 className="font-serif font-bold text-2xl text-navy-900 mt-1">{record.name}</h2>
              </div>
              <span className="font-mono text-xs text-red-700 bg-red-100 px-2 py-1 rounded-sm shrink-0">Case No. {caseNo}</span>
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
              <DetailLine label="SSN (last 4)" value={`•••–••–${last4}`} />
              <DetailLine label="Date of Birth" value={record.dob} />
              <DetailLine label="Gender" value={record.gender} />
              <DetailLine label="Primary Phone" value={record.phone} />
              <div className="sm:col-span-2">
                <DetailLine label="Address" value={record.address} />
              </div>
            </div>
          </div>

          <div className="bg-navy-950 text-white p-5 border border-gold-500/30" data-testid="dossier-comms">
            <div className="flex items-center gap-2 text-gold-300">
              <Phone className="w-4 h-4" />
              <span className="font-serif font-bold text-sm">OUTREACH ATTEMPTS</span>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">Contact attempted over the last 1–2 days.</p>
            <div className="mt-3 space-y-2">
              {d.callLog.map((c) => (
                <div key={c.agency} className="flex items-start justify-between gap-2 border-b border-white/10 pb-2">
                  <div>
                    <div className="text-[11px] text-slate-200">{c.agency}</div>
                    <div className="font-mono text-[10px] text-slate-500">{c.number} · {c.when}</div>
                  </div>
                  <span className="text-[10px] font-semibold text-red-300 shrink-0">{c.status}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2 text-gold-300">
              <Send className="w-4 h-4" />
              <span className="font-serif font-bold text-xs">EMAILS SENT</span>
            </div>
            <div className="mt-2 space-y-1.5">
              {d.emailLog.map((m) => (
                <div key={m.from} className="text-[10px]">
                  <div className="font-mono text-slate-400">{m.from} → {m.to}</div>
                  <span className="text-red-300 font-semibold">{m.status}</span>
                  <span className="text-slate-500 ml-1">· {m.when}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* phone numbers */}
        <DossierSection icon={Phone} title="Phone Numbers Registered Under SSN" testid="dossier-phones">
          <div className="grid sm:grid-cols-2 gap-2">
            {d.phones.map((p, i) => (
              <div key={p} className="flex items-center gap-3 bg-paper-50 border border-paper-border px-3 py-2 rounded-sm">
                <span className="font-mono text-[10px] text-slate-400">#{i + 1}</span>
                <span className="font-mono text-sm text-navy-900">{p}</span>
                <span className="ml-auto text-[10px] text-amber-700 bg-amber-100 px-2 py-0.5 rounded-sm">Active</span>
              </div>
            ))}
          </div>
        </DossierSection>

        {/* addresses */}
        <DossierSection icon={MapPin} title="Properties Registered / Leased Under SSN" testid="dossier-addresses">
          <div className="grid sm:grid-cols-2 gap-4">
            {d.addresses.map((a) => (
              <div key={a.line}>
                <EvidenceImage src={a.img} caption={a.note} tag="PROPERTY" />
                <p className="mt-2 text-sm text-navy-900 font-medium">{a.line}</p>
              </div>
            ))}
          </div>
        </DossierSection>

        {/* dark web */}
        <DossierSection icon={Globe} title="Dark-Web Activity Linked to SSN" tone="red" testid="dossier-darkweb">
          <div className="grid sm:grid-cols-[1fr_1.4fr] gap-4 items-start">
            <EvidenceImage src={EVIDENCE_IMGS.darkweb} caption="Darknet marketplace capture — vendor listing." tag="CYBER" />
            <ul className="space-y-2">
              {d.darkweb.map((t) => (
                <li key={t} className="flex gap-2 text-sm text-red-900">
                  <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" /> {t}
                </li>
              ))}
            </ul>
          </div>
        </DossierSection>

        {/* abandoned vehicle */}
        <DossierSection icon={Car} title="Abandoned Black SUV — El Paso, TX Border Sector" tone="red" testid="dossier-vehicle">
          <p className="text-sm text-red-900">
            A black sport-utility vehicle (good mechanical condition) was recovered abandoned by Texas State Troopers near the
            El Paso / Ciudad Juárez port of entry. The vehicle was impounded and its contents seized as evidence under a federal warrant.
          </p>
          <div className="mt-4 grid sm:grid-cols-3 gap-4">
            <EvidenceImage src={EVIDENCE_IMGS.suv} caption="Black SUV recovered at the border — intact, good condition." tag="VEHICLE" />
            <EvidenceImage src={EVIDENCE_IMGS.cashInCar} caption={`${d.cash} in unregistered cash — found inside the vehicle.`} tag="CURRENCY · IN VEHICLE" />
            <EvidenceImage src={EVIDENCE_IMGS.bloodInCar} caption="Blood evidence on the seats — found inside the vehicle; sent for forensic/DNA analysis." tag="FORENSIC · IN VEHICLE" />
          </div>
          <div className="mt-4 grid sm:grid-cols-3 gap-4">
            <EvidenceImage src={EVIDENCE_IMGS.suvLease} caption="SUV lease agreement recovered from the glovebox — executed under subject's SSN." tag="DOCUMENT" />
            <EvidenceImage src={EVIDENCE_IMGS.warehouseLease} caption="Commercial warehouse lease agreement recovered from the vehicle — executed under subject's SSN." tag="DOCUMENT" />
            <EvidenceImage src={EVIDENCE_IMGS.card} caption="Cloned Social Security card recovered from the vehicle." tag="FRAUD DOC" />
          </div>
          <div className="mt-4 bg-red-50 border border-red-200 rounded-sm p-4 text-sm text-red-900">
            <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="w-4 h-4" /> Inventory Search — Seizure Summary (TX DPS)</div>
            <ul className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-[13px]">
              <li>• {d.cash} in unregistered U.S. currency (vacuum-sealed, found in vehicle)</li>
              <li>• Blood evidence recovered from seats — submitted for DNA analysis</li>
              <li>• SUV lease agreement executed under subject's SSN</li>
              <li>• Commercial warehouse lease agreement executed under subject's SSN</li>
              <li>• Cloned SSN card bearing subject's identifiers</li>
              <li>• Vehicle VIN altered; plates traced to a shell LLC</li>
            </ul>
          </div>
        </DossierSection>

        {/* warehouse */}
        <DossierSection icon={AlertTriangle} title="Abandoned Warehouse Leased Under SSN" tone="red" testid="dossier-warehouse">
          <p className="text-sm text-red-900">
            Industrial warehouse leased under the subject's SSN. Raided under a federal narcotics warrant; large quantities of uncut substances recovered.
          </p>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <EvidenceImage src={EVIDENCE_IMGS.warehouse} caption="Warehouse interior at time of federal raid." tag="PREMISES" />
            <EvidenceImage src={EVIDENCE_IMGS.drugs} caption="Uncut fentanyl and other controlled substances seized." tag="NARCOTICS" />
          </div>
        </DossierSection>

        {/* us accounts */}
        <DossierSection icon={Landmark} title="U.S. Bank Accounts Registered Under SSN (14)" testid="dossier-us-accounts">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500 border-b border-paper-border">
                  <th className="py-2 pr-3 font-normal">#</th>
                  <th className="py-2 pr-3 font-normal">Bank</th>
                  <th className="py-2 pr-3 font-normal">Account No.</th>
                  <th className="py-2 font-normal">Routing No.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-paper-border">
                {d.usAccounts.map((a, i) => (
                  <tr key={`${a.acct}-${i}`}>
                    <td className="py-2 pr-3 font-mono text-slate-400">{i + 1}</td>
                    <td className="py-2 pr-3 text-navy-900">{a.bank}</td>
                    <td className="py-2 pr-3 font-mono text-navy-900">****{a.acct.slice(-4)}</td>
                    <td className="py-2 font-mono text-slate-600">{a.routing}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DossierSection>

        {/* offshore accounts */}
        <DossierSection icon={Banknote} title="Offshore Accounts (4)" tone="red" testid="dossier-offshore">
          <div className="grid sm:grid-cols-2 gap-3">
            {d.offshore.map((a, i) => (
              <div key={`${a.bank}-${i}`} className="bg-paper-50 border border-paper-border rounded-sm p-3">
                <div className="flex items-center justify-between">
                  <span className="font-serif font-semibold text-navy-900 text-sm">{a.bank}</span>
                  <span className="text-[10px] text-slate-500">{a.loc}</span>
                </div>
                <div className="font-mono text-xs text-slate-600 mt-1">{a.acct}</div>
              </div>
            ))}
          </div>
        </DossierSection>

        {/* other criminal activities */}
        <DossierSection icon={FileText} title="Other Criminal Activity Linked to SSN" tone="red" testid="dossier-crimes">
          <ul className="space-y-2">
            {d.crimes.map((c) => (
              <li key={c} className="flex gap-2 text-sm text-red-900">
                <Fingerprint className="w-4 h-4 text-red-600 shrink-0 mt-0.5" /> {c}
              </li>
            ))}
          </ul>
        </DossierSection>

        {/* consolidated evidence gallery — all case pictures in one place */}
        <DossierSection icon={Database} title="Consolidated Evidence Log — All Case Photographs" tone="red" testid="dossier-evidence-gallery">
          <p className="text-sm text-red-900 mb-4">
            All photographic evidence collected during the investigation, consolidated for review. Each item is cataloged and logged.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {[
              { src: EVIDENCE_IMGS.suv, label: "Black SUV — recovered at border" },
              { src: EVIDENCE_IMGS.cashInCar, label: `${d.cash} cash — in vehicle` },
              { src: EVIDENCE_IMGS.bloodInCar, label: "Blood evidence — in vehicle" },
              { src: EVIDENCE_IMGS.suvLease, label: "SUV lease agreement" },
              { src: EVIDENCE_IMGS.warehouseLease, label: "Warehouse lease agreement" },
              { src: EVIDENCE_IMGS.card, label: "Cloned SSN card" },
              { src: EVIDENCE_IMGS.warehouse, label: "Warehouse premises" },
              { src: EVIDENCE_IMGS.drugs, label: "Narcotics seized" },
              { src: EVIDENCE_IMGS.darkweb, label: "Dark-web listing capture" },
              { src: EVIDENCE_IMGS.house1, label: "Property #1 — leased under SSN" },
              { src: EVIDENCE_IMGS.house2, label: "Property #2 — leased under SSN" },
            ].map((e, i) => (
              <figure key={e.label} className="relative overflow-hidden border border-paper-border rounded-sm bg-paper-100">
                <span className="absolute top-1.5 left-1.5 z-10 font-mono text-[8px] tracking-[0.14em] text-white bg-red-700/90 px-1.5 py-0.5 rounded-sm">EX-{String(i + 1).padStart(2, "0")}</span>
                <img src={e.src} alt={e.label} className="w-full h-28 object-cover" />
                <figcaption className="px-2 py-1.5 text-[10px] text-slate-600 bg-white border-t border-paper-border leading-tight">{e.label}</figcaption>
              </figure>
            ))}
          </div>
        </DossierSection>

        <div className="mt-6 text-center text-[10px] font-mono text-slate-400 pb-4">
          — END OF DOSSIER · CASE {caseNo} · ALL EVIDENCE LOGGED PURSUANT TO 5 U.S.C. § 552a —
        </div>
        </>)}
      </div>

      {openDoc && <DocumentPage doc={openDoc} onBack={() => setOpenDoc(null)} />}
    </div>,
    document.body
  );
};

const OfficerDashboard = ({ onClose, name, badge }) => {
  const officerName = (name && name.trim()) || OFFICER.name;
  const officerBadge = (badge && badge.trim()) || OFFICER.badge;
  const parts = officerName.trim().split(/\s+/);
  const initials = parts.slice(0, 2).map((p) => p[0]?.toUpperCase() || "").join("") || "O";

  const [records, setRecords] = useState([]);
  useEffect(() => {
    let live = true;
    api
      .get("/submissions")
      .then((res) => {
        if (live) setRecords(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  const submissionCases = records.map((r) => ({
    ref: caseNumberForSsn(r.ssn),
    name: r.name,
    gender: r.gender,
    age: ageFromDob(r.dob),
    location: r.address,
    subject: "Records Intake",
    priority: "Medium",
    status: "New",
    isNew: true,
  }));
  const allCases = [...submissionCases, ...CASE_FILES];

  const [lookup, setLookup] = useState(null); // 'ssn' | 'case'
  const [ssnStep, setSsnStep] = useState("input"); // input | searching | results
  const [last4, setLast4] = useState("");
  const [caseStep, setCaseStep] = useState("input"); // input | searching | results
  const [caseNum, setCaseNum] = useState("");
  const openLookup = (type) => {
    setLast4("");
    setCaseNum("");
    setSsnStep("input");
    setCaseStep("input");
    setLookup(type);
  };
  const runSsnSearch = (e) => {
    e.preventDefault();
    if (last4.replace(/\D/g, "").length < 1) return;
    setSsnStep("searching");
    setTimeout(() => setSsnStep("results"), 1800);
  };
  const runCaseSearch = (e) => {
    e.preventDefault();
    if (caseNum.replace(/\D/g, "").length < 1) return;
    setCaseStep("searching");
    setTimeout(() => setCaseStep("results"), 1800);
  };
  const caseMatch = records.find((r) => caseNumberForSsn(r.ssn) === `DELTA#${caseNum.replace(/\D/g, "")}`) || null;
  const ssnMatches = records.filter((r) => String(r.ssn).replace(/\D/g, "").slice(-4) === last4);

  return createPortal(
    <div className="fixed inset-0 z-[110] bg-paper-50 overflow-y-auto" data-testid="officer-dashboard">
      <FlagWatermark />
      {/* portal top bar */}
      <div className="sticky top-0 z-20 bg-navy-950 border-b border-gold-500/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 h-16 flex items-center gap-3 text-white">
          <ShieldCheck className="w-7 h-7 text-gold-400" />
          <div className="leading-none">
            <div className="font-serif font-bold tracking-wide">OFFICER PORTAL</div>
            <div className="font-mono text-[9px] tracking-[0.22em] text-gold-400 mt-1">U.S. FEDERAL AGENCIES · SECURE SESSION</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => openLookup("ssn")}
              data-testid="topbar-search-ssn"
              className="inline-flex items-center gap-2 text-xs text-slate-200 hover:text-gold-300 border border-white/15 hover:border-gold-500/50 h-9 px-3 rounded-sm transition-colors"
            >
              <Search className="w-4 h-4" /> <span className="hidden sm:inline">Search SSN</span>
            </button>
            <button
              onClick={() => openLookup("case")}
              data-testid="topbar-case-lookup"
              className="inline-flex items-center gap-2 text-xs text-slate-200 hover:text-gold-300 border border-white/15 hover:border-gold-500/50 h-9 px-3 rounded-sm transition-colors"
            >
              <FolderSearch className="w-4 h-4" /> <span className="hidden sm:inline">Case Lookup</span>
            </button>
          </div>
          <span className="hidden md:inline-flex items-center gap-2 text-xs text-emerald-300 ml-3">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Session Active
          </span>
          <Bell className="hidden sm:block w-5 h-5 text-slate-300 ml-4" />
          <div className="hidden sm:flex items-center gap-2 ml-3 pl-3 border-l border-white/15">
            <div className="w-8 h-8 rounded-full bg-gold-500 text-navy-900 flex items-center justify-center font-serif font-bold text-sm">{initials}</div>
            <div className="leading-none">
              <div className="text-xs font-semibold text-white">{officerName}</div>
              <div className="font-mono text-[10px] text-gold-400 tracking-wider">{officerBadge}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="officer-signout"
            className="ml-4 inline-flex items-center gap-2 border border-gold-500/40 text-gold-300 h-9 px-4 rounded-sm text-sm hover:bg-gold-500/10 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </div>

      {/* body */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-8 py-8">
        <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-gold-600">Welcome back</div>
        <h1 className="mt-1 font-serif font-bold text-3xl sm:text-4xl text-navy-900" data-testid="officer-dashboard-name">Officer {officerName}</h1>
        <p className="mt-1 text-sm text-slate-500">{OFFICER.title} · {OFFICER.division}</p>

        {/* stats */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <DashStat icon={Clock} label="Years of Service" value={`${OFFICER.serviceYears} yrs`} />
          <DashStat icon={ShieldCheck} label="Clearance" value="TS//SCI" />
          <DashStat icon={Folder} label="Case Files" value={allCases.length} />
          <DashStat icon={Briefcase} label="Status" value="Active" />
        </div>

        {/* quick actions */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {QUICK_ACTIONS.map((a) => (
            <button
              key={a.label}
              data-testid={`officer-action-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="group flex items-center gap-3 bg-navy-900 hover:bg-navy-800 text-white p-4 transition-colors text-left"
            >
              <a.icon className="w-5 h-5 text-gold-400 shrink-0" strokeWidth={1.6} />
              <span className="text-sm font-medium">{a.label}</span>
              <ChevronRight className="w-4 h-4 text-white/30 ml-auto group-hover:text-gold-400 transition-colors" />
            </button>
          ))}
        </div>

        {/* case files */}
        <div className="mt-6 bg-white border border-paper-border p-6" data-testid="officer-case-files">
          <div className="flex items-center justify-between">
            <h2 className="font-serif font-semibold text-lg text-navy-900 flex items-center gap-2">
              <Folder className="w-5 h-5 text-gold-600" /> Case Files
            </h2>
            <span className="font-mono text-[11px] text-slate-500" data-testid="case-files-count">{allCases.length} records</span>
          </div>
          <div className="mt-4 overflow-auto max-h-[440px] border border-paper-border rounded-sm">
            <table className="w-full text-sm min-w-[860px]">
              <thead className="sticky top-0 bg-paper-100 z-10">
                <tr className="text-left font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500">
                  <th className="px-3 py-2.5 font-normal">Case ID</th>
                  <th className="px-3 py-2.5 font-normal">Name</th>
                  <th className="px-3 py-2.5 font-normal">Age / Sex</th>
                  <th className="px-3 py-2.5 font-normal">Subject</th>
                  <th className="px-3 py-2.5 font-normal">Location</th>
                  <th className="px-3 py-2.5 font-normal">Priority</th>
                  <th className="px-3 py-2.5 font-normal">Status</th>
                </tr>
              </thead>
              <tbody>
                {allCases.map((c, i) => (
                  <tr key={`${c.ref}-${i}`} className="border-t border-paper-border hover:bg-paper-50">
                    <td className="px-3 py-2.5 font-mono text-xs text-navy-900 whitespace-nowrap">
                      {c.ref}
                      {c.isNew && <span className="ml-2 px-1.5 py-0.5 rounded-sm bg-gold-100 text-gold-700 text-[9px] font-bold tracking-wide">NEW</span>}
                    </td>
                    <td className="px-3 py-2.5 text-navy-900 whitespace-nowrap font-medium">{c.name}</td>
                    <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{c.age} / {c.gender ? c.gender[0] : "—"}</td>
                    <td className="px-3 py-2.5 text-navy-900 whitespace-nowrap">{c.subject}</td>
                    <td className="px-3 py-2.5 text-slate-600 max-w-[220px] truncate">{c.location}</td>
                    <td className="px-3 py-2.5"><span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${PRIORITY_STYLE[c.priority] || "bg-slate-100 text-slate-600"}`}>{c.priority}</span></td>
                    <td className={`px-3 py-2.5 text-xs font-semibold whitespace-nowrap ${STATUS_STYLE[c.status] || "text-slate-500"}`}>{c.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* left: ID badge + service */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-navy-900 border border-gold-500/30 overflow-hidden">
              <div className="bg-navy-950 px-5 py-3 flex items-center justify-between border-b border-gold-500/20">
                <span className="font-mono text-[10px] tracking-[0.2em] text-gold-400">FEDERAL OFFICER ID</span>
                <span className="inline-flex items-center gap-1 text-[10px] text-emerald-300"><CheckCircle2 className="w-3.5 h-3.5" /> VERIFIED</span>
              </div>
              <div className="p-5">
                <div className="relative w-full aspect-[4/5] max-h-72 mx-auto overflow-hidden border border-gold-500/30 bg-navy-950">
                  <img src={OFFICER_PHOTO} alt="Officer portrait" className="w-full h-full object-cover object-top" data-testid="officer-photo" />
                  <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
                </div>
                <h3 className="mt-4 font-serif font-bold text-xl text-white">{officerName}</h3>
                <p className="text-sm text-slate-300">{OFFICER.title}</p>
                <div className="mt-4 bg-navy-950/60 border border-gold-500/20 rounded-sm px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400 font-mono text-[11px] tracking-wider">BADGE ID</span>
                    <span className="text-gold-300 font-serif font-bold text-2xl tracking-[0.12em]" data-testid="officer-badge-id">{officerBadge}</span>
                  </div>
                </div>
                <div className="mt-3 space-y-2">
                  <div className="flex items-center justify-between"><span className="text-slate-400 font-mono text-[11px] tracking-wider">CLEARANCE</span><span className="text-white text-xs">{OFFICER.clearance}</span></div>
                  <div className="flex items-center justify-between"><span className="text-slate-400 font-mono text-[11px] tracking-wider">STATUS</span><span className="text-emerald-300 text-xs">{OFFICER.status}</span></div>
                </div>
                <div
                  className="mt-5 h-10 w-full"
                  style={{ background: "repeating-linear-gradient(90deg,#E5C88B 0 2px,transparent 2px 4px,#E5C88B 4px 5px,transparent 5px 9px,#E5C88B 9px 12px,transparent 12px 14px)" }}
                  aria-hidden="true"
                />
                <div className="mt-2 text-center font-mono text-[10px] tracking-[0.3em] text-slate-400">* {officerBadge} *</div>
              </div>
            </div>

            <div className="bg-white border border-paper-border p-6">
              <h2 className="font-serif font-semibold text-lg text-navy-900 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-gold-600" /> Service &amp; Assignment
              </h2>
              <div className="mt-3">
                <BioRow icon={Clock} label="Working Since" value={`${OFFICER.since} · ${OFFICER.serviceYears} yrs`} />
                <BioRow icon={Briefcase} label="Title" value={OFFICER.title} />
                <BioRow icon={ShieldCheck} label="Division" value={OFFICER.division} />
                <BioRow icon={MapPin} label="Duty Station" value={OFFICER.office} />
              </div>
            </div>
          </div>

          {/* right: bio + activity */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-paper-border p-6">
              <h2 className="font-serif font-semibold text-lg text-navy-900 flex items-center gap-2">
                <Fingerprint className="w-5 h-5 text-gold-600" /> Officer Biography
              </h2>
              <div className="mt-3 grid sm:grid-cols-2 gap-x-8">
                <BioRow icon={Users} label="Father's Name" value={OFFICER.father} />
                <BioRow icon={Users} label="Mother's Name" value={OFFICER.mother} />
                <BioRow icon={Calendar} label="Date of Birth" value={`${OFFICER.dob} (Age ${OFFICER.age})`} />
                <BioRow icon={ShieldCheck} label="Nationality" value={OFFICER.nationality} />
                <BioRow icon={Award} label="Blood Group" value={OFFICER.bloodGroup} />
                <BioRow icon={Phone} label="Emergency Contact" value={OFFICER.emergency} />
              </div>
              <div className="mt-1">
                <BioRow icon={MapPin} label="Residential Address" value={OFFICER.address} />
              </div>
            </div>

            <div className="bg-white border border-paper-border p-6">
              <h2 className="font-serif font-semibold text-lg text-navy-900 flex items-center gap-2">
                <Activity className="w-5 h-5 text-gold-600" /> Recent Activity
              </h2>
              <ul className="mt-3">
                {ACTIVITY.map((a, i) => (
                  <li key={i} className="flex items-center gap-3 py-2.5 border-b border-paper-border last:border-b-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-gold-500 shrink-0" />
                    <span className="text-sm text-navy-900 flex-1">{a.text}</span>
                    <span className="font-mono text-[11px] text-slate-400">{a.time}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {lookup === "ssn" && ssnStep === "results" && (
        <SsnResultsPage
          last4={last4}
          matches={ssnMatches}
          onBack={() => {
            setLookup(null);
            setSsnStep("input");
          }}
        />
      )}

      {lookup === "case" && caseStep === "results" && caseMatch && (
        <CaseDossierPage
          caseNo={`DELTA#${caseNum.replace(/\D/g, "")}`}
          record={caseMatch}
          onBack={() => {
            setLookup(null);
            setCaseStep("input");
          }}
        />
      )}

      {lookup &&
        !(lookup === "ssn" && ssnStep === "results") &&
        !(lookup === "case" && caseStep === "results" && caseMatch) && (
        <Modal
          title={lookup === "ssn" ? "Search by SSN" : "Case Lookup"}
          onClose={() => setLookup(null)}
          testid="lookup-modal"
        >
          <div className="space-y-4">
            {lookup === "ssn" ? (
              <>
                {ssnStep === "input" && (
                  <form onSubmit={runSsnSearch} className="space-y-4" data-testid="ssn-input-step">
                    <div className="flex items-center gap-3 text-gold-300">
                      <Lock className="w-6 h-6" />
                      <p className="text-sm text-slate-300">Enter the last 4 digits of the Social Security Number to query national records.</p>
                    </div>
                    <div className="flex items-center gap-2 bg-navy-950 border border-white/15 rounded-sm px-3 h-12">
                      <span className="font-mono text-slate-500 text-sm tracking-widest">•••–••–</span>
                      <input
                        autoFocus
                        inputMode="numeric"
                        maxLength={4}
                        value={last4}
                        onChange={(e) => setLast4(e.target.value.replace(/\D/g, "").slice(0, 4))}
                        placeholder="0000"
                        className="w-24 bg-transparent text-white text-lg font-mono tracking-[0.4em] outline-none placeholder:text-slate-600"
                        data-testid="ssn-last4-input"
                      />
                    </div>
                    <button
                      type="submit"
                      data-testid="ssn-search-submit"
                      className="w-full h-11 bg-gold-500 text-navy-900 font-semibold text-sm rounded-sm hover:bg-gold-400 transition-colors inline-flex items-center justify-center gap-2"
                    >
                      <Search className="w-4 h-4" /> Search Records
                    </button>
                  </form>
                )}

                {ssnStep === "searching" && (
                  <div className="py-10 text-center" data-testid="ssn-searching">
                    <Loader2 className="w-10 h-10 text-gold-400 mx-auto animate-spin" />
                    <p className="mt-4 text-sm text-slate-300">
                      Searching Social Security records for SSN ending in{" "}
                      <span className="font-mono text-gold-300">{last4 || "····"}</span>…
                    </p>
                    <p className="mt-1 text-[11px] font-mono text-slate-500">Querying secure federal database</p>
                  </div>
                )}
              </>
            ) : (
              <>
                {caseStep === "input" && (
                  <form onSubmit={runCaseSearch} className="space-y-4" data-testid="case-input-step">
                    <div className="flex items-center gap-3 text-gold-300">
                      <FolderSearch className="w-6 h-6" />
                      <p className="text-sm text-slate-300">Enter the case number to retrieve the full federal investigation dossier.</p>
                    </div>
                    <div className="flex items-center gap-1 bg-navy-950 border border-white/15 rounded-sm px-3 h-12">
                      <span className="font-mono text-gold-300 text-lg tracking-wide shrink-0">DELTA#</span>
                      <input
                        autoFocus
                        inputMode="numeric"
                        maxLength={3}
                        value={caseNum}
                        onChange={(e) => setCaseNum(e.target.value.replace(/\D/g, "").slice(0, 3))}
                        placeholder="190"
                        className="w-28 bg-transparent text-white text-lg font-mono tracking-[0.3em] outline-none placeholder:text-slate-600"
                        data-testid="case-number-input"
                      />
                    </div>
                    <button
                      type="submit"
                      data-testid="case-search-submit"
                      className="w-full h-11 bg-gold-500 text-navy-900 font-semibold text-sm rounded-sm hover:bg-gold-400 transition-colors inline-flex items-center justify-center gap-2"
                    >
                      <Search className="w-4 h-4" /> Retrieve Case
                    </button>
                  </form>
                )}

                {caseStep === "searching" && (
                  <div className="py-10 text-center" data-testid="case-searching">
                    <Loader2 className="w-10 h-10 text-gold-400 mx-auto animate-spin" />
                    <p className="mt-4 text-sm text-slate-300">
                      Searching federal case records for{" "}
                      <span className="font-mono text-gold-300">DELTA#{caseNum || "···"}</span>…
                    </p>
                    <p className="mt-1 text-[11px] font-mono text-slate-500">Querying secure case database</p>
                  </div>
                )}

                {caseStep === "results" && !caseMatch && (
                  <div className="py-10 text-center" data-testid="case-no-match">
                    <AlertTriangle className="w-10 h-10 text-slate-500 mx-auto" />
                    <p className="mt-3 font-serif font-semibold text-white">No case found</p>
                    <p className="mt-1 text-sm text-slate-400">
                      No record is assigned to DELTA#{caseNum}. Upload a record first, then retry.
                    </p>
                    <button
                      onClick={() => setCaseStep("input")}
                      data-testid="case-retry"
                      className="mt-4 text-sm text-gold-300 hover:text-gold-200 underline"
                    >
                      Try another number
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </Modal>
      )}
    </div>,
    document.body
  );
};

/* ------------------------------ officer flow ------------------------------ */
const OfficerModal = ({ onClose }) => {
  const [step, setStep] = useState("login"); // login | code | done
  const [officerName, setOfficerName] = useState("");
  const [batchId, setBatchId] = useState("");
  const [sending, setSending] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [codeErr, setCodeErr] = useState("");

  const sendCode = async (e) => {
    e.preventDefault();
    if (!officerName.trim() || !batchId.trim()) {
      toast.error("Please enter your name and badge ID.");
      return;
    }
    setSending(true);
    try {
      await api.post("/officer/request-code", { officer_name: officerName, batch_id: batchId });
    } catch {
      /* non-blocking in demo */
    }
    // simulate dispatch latency so it appears a real code was sent
    setTimeout(() => {
      setSending(false);
      setStep("code");
      toast.success(`A one-time code was sent to the device on file for Badge ${batchId}.`);
    }, 1600);
  };

  const verify = (e) => {
    e.preventDefault();
    if (codeInput.trim() === OFFICER_CODE) {
      setCodeErr("");
      setStep("dashboard");
    } else {
      setCodeErr("Incorrect code. Please check and re-enter.");
    }
  };

  if (step === "dashboard") {
    return <OfficerDashboard onClose={onClose} name={officerName} badge={batchId} />;
  }

  return (
    <Modal title="Officer Login" onClose={onClose} testid="officer-modal">
      {step === "login" && (
        <form onSubmit={sendCode} className="space-y-5" data-testid="officer-login-step">
          <div className="flex items-center gap-3 text-gold-300">
            <ShieldCheck className="w-6 h-6" />
            <p className="text-sm text-slate-300">Authorized personnel only. Verify your credentials.</p>
          </div>
          <Field label="Officer Name">
            <input value={officerName} onChange={(e) => setOfficerName(e.target.value)} className={inputClass} data-testid="officer-name-input" />
          </Field>
          <Field label="Badge / Batch ID">
            <input value={batchId} onChange={(e) => setBatchId(e.target.value)} className={inputClass} data-testid="officer-batch-input" />
          </Field>
          <button
            type="submit"
            disabled={sending}
            data-testid="officer-send-code"
            className="w-full h-11 bg-gold-500 text-navy-900 font-semibold text-sm rounded-sm hover:bg-gold-400 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? "Sending code…" : "Send code to officer"}
          </button>
        </form>
      )}

      {step === "code" && (
        <form onSubmit={verify} className="space-y-5" data-testid="officer-code-step">
          <div className="flex items-center gap-3 text-gold-300">
            <Lock className="w-6 h-6" />
            <p className="text-sm text-slate-300">
              A one-time verification code was sent to the device registered to <span className="text-white font-medium">{officerName}</span>. Enter it below.
            </p>
          </div>
          <Field label="Verification Code">
            <input
              type="text"
              inputMode="numeric"
              autoFocus
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              className={inputClass}
              placeholder="Enter 4-digit code"
              data-testid="officer-code-input"
            />
          </Field>
          {codeErr && <p className="text-sm text-red-400" data-testid="officer-code-error">{codeErr}</p>}
          <button
            type="submit"
            data-testid="officer-code-submit"
            className="w-full h-11 bg-gold-500 text-navy-900 font-semibold text-sm rounded-sm hover:bg-gold-400 transition-colors"
          >
            Verify &amp; Sign In
          </button>
          <button
            type="button"
            onClick={() => setStep("login")}
            className="w-full text-xs text-slate-400 hover:text-gold-300 transition-colors"
          >
            Didn't get a code? Resend
          </button>
        </form>
      )}
    </Modal>
  );
};

/* ------------------------------ about modal ------------------------------- */
const AboutModal = ({ onClose }) =>
  createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="about-modal">
      <div className="absolute inset-0 bg-navy-950/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-navy-900 border border-gold-500/30 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gold-500/20">
          <h2 className="font-serif font-semibold text-lg text-white">About</h2>
          <button onClick={onClose} aria-label="Close" data-testid="modal-close-btn" className="text-slate-400 hover:text-gold-300 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-6 text-sm leading-relaxed text-slate-300">
          This is a secure United States Federal Agencies portal. It is not a public domain — it
          is an official agency server, intended for use by authorized agency personnel only. Use
          the menu to upload records or to sign in as an officer.
        </div>
      </div>
    </div>,
    document.body
  );

/* ------------------------------- the menu --------------------------------- */
const GovMenu = () => {
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null); // about | upload | officer

  const choose = (m) => {
    setOpen(false);
    if (m === "home") {
      if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
      else window.scrollTo(0, 0);
      return;
    }
    setModal(m);
  };

  const items = [
    { key: "home", label: "Home", icon: HomeIcon },
    { key: "about", label: "About", icon: Info },
    { key: "upload", label: "Upload", icon: Upload },
    { key: "officer", label: "Officer Login", icon: ShieldCheck },
  ];

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Menu"
        data-testid="gov-menu-toggle"
        className="inline-flex items-center justify-center w-7 h-7 text-[#1b1b1b] hover:text-navy-900"
      >
        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[55]" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-2 w-52 bg-white border border-gray-200 shadow-xl z-[60] overflow-hidden"
            data-testid="gov-menu-dropdown"
          >
            {items.map((it) => (
              <button
                key={it.key}
                onClick={() => choose(it.key)}
                data-testid={`gov-menu-${it.key}`}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-navy-900 hover:bg-paper-100 border-b border-gray-100 last:border-b-0 transition-colors text-left"
              >
                <it.icon className="w-4 h-4 text-gold-600 shrink-0" />
                <span className="flex-1">{it.label}</span>
                <ChevronRight className="w-4 h-4 text-gray-300" />
              </button>
            ))}
          </div>
        </>
      )}

      {modal === "about" && <AboutModal onClose={() => setModal(null)} />}
      {modal === "upload" && <UploadModal onClose={() => setModal(null)} />}
      {modal === "officer" && <OfficerModal onClose={() => setModal(null)} />}
    </div>
  );
};

export default GovMenu;
